import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { resetToast } from '@/store/slices/toastSlice';
import type { ToastSeverity } from '@/types';

const severityConfig: Record<
  ToastSeverity,
  { icon: typeof CheckCircle2; accent: string; iconBg: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle2,
    accent: 'border-l-emerald-500 bg-emerald-50',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  error: {
    icon: XCircle,
    accent: 'border-l-red-500 bg-red-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  warning: {
    icon: AlertTriangle,
    accent: 'border-l-amber-500 bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  info: {
    icon: Info,
    accent: 'border-l-sky-500 bg-sky-50',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
};

const severityMessageColor: Record<ToastSeverity, string> = {
  success: 'text-emerald-800',
  error: 'text-red-800',
  warning: 'text-amber-800',
  info: 'text-sky-800',
};

export function Toast() {
  const dispatch = useAppDispatch();
  const { open, message, severity } = useAppSelector((s: RootState) => s.toast);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!open) {
      setProgress(100);
      return;
    }
    const duration = 4000;
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
    }, 50);
    const t = setTimeout(() => dispatch(resetToast()), duration);
    return () => {
      clearTimeout(t);
      clearInterval(interval);
    };
  }, [open, dispatch]);

  if (!open) return null;

  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 w-full max-w-sm overflow-hidden rounded-xl border border-slate-200/80 border-l-4 ${config.accent} shadow-lg shadow-slate-900/10`}
      role="alert"
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${config.iconBg}`}>
          <Icon className={`h-5 w-5 ${config.iconColor}`} strokeWidth={2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className={`text-sm font-medium ${severityMessageColor[severity]}`}>{message}</p>
        </div>
        <button
          type="button"
          onClick={() => dispatch(resetToast())}
          className="-m-1.5 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/60 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="h-0.5 bg-slate-200/60">
        <div
          className="h-full bg-slate-400/50 transition-all duration-75 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
